import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, Tabs, Tab, IconButton } from '@mui/material';
import DialogContentText from '@mui/material/DialogContentText';
import Text from '../text';
import { DialogTitle } from '@mui/material';
import TableColumns from '../table-settings/TableColumns';
import UploadResults from '../table-settings/UploadResults';
import ExportResults from '../table-settings/ExportResults';
import LocationsTests from '../table-settings/LocationsTest';
import AlarmSettings from '../table-settings/AlarmSettings';
import ManageUsers from '../table-settings/ManageUsers';
import { DataPoint } from 'utils/models/data-point';
import { DataRow } from 'utils/models/data-row';
import { addEventLog } from 'utils/functions/eventLogFunctions';
import { getTestResults } from 'utils/services/idbService';
import { TABLE_SETTINGS_TABS } from '../menu/table-settings';
interface TableSettingsDialogProps {
	open: boolean;
	currentTab: number;
	onClose: () => void;
	setCurrentTab: React.Dispatch<React.SetStateAction<number>>;
	siteName?: string;
	dataPoints?: DataPoint[];
	isOwner?: boolean;
	siteId: number;
	tableColumnSettingsAllVOs: TableColumnSettingsAllVO[] | undefined;
	viewMode: string;
	// ... any other props
}

const TableSettingsDialog: React.FC<TableSettingsDialogProps> = ({
	open,
	currentTab,
	onClose,
	setCurrentTab,
	siteName,
	dataPoints,
	isOwner,
	siteId,
	tableColumnSettingsAllVOs,
	viewMode
	// ... other props
}) => {
	const [selectedTab, setSelectedTab] = React.useState<number>(currentTab);
	const [notesData, setNotesData] = useState<{ [key: string]: any }>({});
	const [isLoading, setIsLoading] = useState(false);
  	const [isOnline, setIsOnline] = useState(navigator.onLine);
	const [groups, setGroups] = useState<{ sites: any; id: number; name: string }[]>([]);

	useEffect(() => {
		let cancelled = false;

		const loadGroups = async () => {
			const result =
				(await getTestResults(
					`groupsInfo`,
					localStorage.getItem('currentUser') ?? 'unknown'
				)) || [];
			if (!cancelled) {
				setGroups(result);
			}
		};

		loadGroups();

		return () => {
			cancelled = true;
		};
	}, []);

	const siteToGroupMap = useMemo(() => {
		const map = new Map<number, number>();
		groups.forEach((group) => {
			group.sites?.forEach((site: any) => {
				map.set(site.id, group.id);
			});
		});
		return map;
	}, [groups]);

	const currentGroup = useMemo(() => {
		const groupId = siteToGroupMap.get(siteId);
		if (groupId === undefined) return null;
		return groups.find((g) => g.id === groupId) ?? null;
	}, [siteToGroupMap, siteId, groups]);
	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		const value = TABLE_SETTINGS_TABS[newValue] === "Export" ? "Export results" : TABLE_SETTINGS_TABS[newValue] === "Alarms" ? "Alarm settings" : TABLE_SETTINGS_TABS[newValue];
		addEventLog(`${value} opened`, "Site Management", siteId, undefined);
		setSelectedTab(newValue);
	};

	React.useEffect(() => {
		if (open) {
			setSelectedTab(currentTab);
		}
	}, [open, currentTab]);

  React.useEffect(() => {
		const updateOnlineStatus = () => {
			setIsOnline(navigator.onLine);
		};

		window.addEventListener('online', updateOnlineStatus);
		window.addEventListener('offline', updateOnlineStatus);

		return () => {
			window.removeEventListener('online', updateOnlineStatus);
			window.removeEventListener('offline', updateOnlineStatus);
		};
	}, []);
	const validOutputNames = useMemo(() => {
		return (
			Array.from(
				new Set(
					dataPoints
						?.filter((dp) => dp.outputType === 'Output')
						.map((dp) => dp.outputName)
				)
			) || []
		);
	}, [dataPoints]);

	const filteredDataPoints = useMemo(() => {
		return dataPoints?.filter((dp) => dp.outputType === 'Output');
	}, [dataPoints]);

	const rows = useMemo(() => {
		const groupedByDate: { [date: string]: DataRow } = {};

		filteredDataPoints?.forEach((dp) => {
			const date = new Date(dp.timestamp).toISOString().split('T')[0];
			const note = notesData[dp.samplePointId] || '';

			if (!groupedByDate[date]) {
				groupedByDate[date] = {
					date,
					outputAlarm: { [dp.outputName]: dp.outputAlarm },
					allLocations: dp.samplePointName,
					allTests: dp.testkitName,
					notes: note,
					history: dp.id,
					outputs: Object.fromEntries(
						validOutputNames.map((name) => [name, null])
					),
				};
			}

			if (validOutputNames.includes(dp.outputName)) {
				groupedByDate[date].outputs[dp.outputName] = dp.outputValue;
			}
		});

		return Object.values(groupedByDate);
	}, [filteredDataPoints, validOutputNames]);

	return (
		<Dialog
			id="table-settings"
			open={open}
			onClose={(event, reason) => {
				if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
					addEventLog(`Site Table Settings Closed`, "Site Management", siteId, undefined);
					onClose();
				}
			}}
			fullWidth
			maxWidth="lg"
			sx={{
				'& .MuiPaper-root': {
					overflow: 'hidden',
					maxHeight: '82vh',
					position: 'absolute',
					top: '50px',
				},
			}}
		>
			<DialogTitle>
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
					}}
				>
					<Text addStyle="!font-inter !font-bold !text-[#2C2C2C] !text-[24px] !mb-4 !tracking-[0.15px] site-name">
						{siteName}
					</Text>
					<IconButton
						onClick={onClose}
						sx={{ p: 0, mr: '5px', mt: '2px' }}
						className="absolute top-[-8px] right-0"
					>
						<img
							src="/assets/icons/Close.svg"
							alt="Remove"
							className="w-5 h-5 md:block hidden"
						/>
					</IconButton>
				</div>
				<Tabs
					value={selectedTab}
					onChange={handleTabChange}
					variant="scrollable"
					scrollButtons="auto"
					sx={{
						'& .MuiTabs-indicator': {
							backgroundColor: '#0092C8',
						},
						'& .MuiButtonBase-root': {
							fontSize: '14px',
							letterSpacing: '0.5px'
						}
					}}
					className="border-b-[1px] border-[#E5E5E5]"
				>
					{TABLE_SETTINGS_TABS.map((setting) => (
						<Tab key={setting} label={setting} />
					))}
				</Tabs>
			</DialogTitle>
			<DialogContent>
				{selectedTab === TABLE_SETTINGS_TABS.indexOf('Upload results') && (
					<UploadResults />
				)}
				{selectedTab === TABLE_SETTINGS_TABS.indexOf('Sample locations') && (
					<LocationsTests isOwner={isOwner} siteId={siteId} isOnline={isOnline} />
				)}
				{selectedTab === TABLE_SETTINGS_TABS.indexOf('Export results') && (
					<ExportResults 
						siteName={siteName ?? ''} 
						siteId={siteId} 
						onClose={onClose} 
						isOnline={isOnline} 
						group={currentGroup}
						tableColumnSettingsAllVOs={tableColumnSettingsAllVOs} 
						dataPoints={dataPoints}
						viewMode={viewMode}
					/>
				)}
				{selectedTab === TABLE_SETTINGS_TABS.indexOf('Alarm settings') && (
					<AlarmSettings rows={rows} siteId={siteId} isOwner={isOwner} onClose={onClose} isOnline={isOnline}/>
				)}
				{selectedTab === TABLE_SETTINGS_TABS.indexOf('Manage users') && (
					<ManageUsers isOwner={isOwner} siteId={siteId} isOnline={isOnline}/>
				)}
				{selectedTab === TABLE_SETTINGS_TABS.indexOf('Table columns') ? (
					<TableColumns siteId={siteId} onClose={onClose} isOnline={isOnline}/>
				) : (
					<DialogContentText className={`!font-inter !text-[#000] !text-[14px] ${!isOwner && "!mb-4"}`}></DialogContentText>
				)}
			</DialogContent>
			{/* <DialogActions className="!px-[20px] !py-[15px]">
				{selectedTab === settings.indexOf('Manage Users') && (
					<>
						<Button
							onClick={() => {}}
							color="primary"
							className="!text-[#9EACB4]"
						>
							CANCEL
						</Button>
						{isLoading ? (
							<CircularProgress
								sx={{ color: '#0092C8' }}
								size={22}
								className="!mr-5"
							/>
						) : (
							<PrimaryButton
								addStyle="!h-[36px] !w-[68px]"
								onClick={() => {}}
							>
								SAVE
							</PrimaryButton>
						)}
					</>
				)}
			</DialogActions> */}
		</Dialog>
	);
};

export default TableSettingsDialog;
